<template>
  <el-card
    shadow="never"
    class="monitoring-block monitoring-matrix-block"
    :class="urgencyClass"
  >
    <div
      slot="header"
      class="monitoring-block__header"
    >
      <span class="title">{{ $t(definition.titleKey) }}</span>
      <MonitoringBlockHint />
    </div>

    <table class="monitoring-matrix">
      <thead>
        <tr>
          <th v-if="definition.rows" />
          <th
            v-for="col in definition.columns"
            :key="col.key"
          >
            {{ $t(col.labelKey) }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row ? row.key : 'single'"
        >
          <th
            v-if="definition.rows"
            class="monitoring-matrix__row-label"
          >
            {{ $t(row.labelKey) }}
          </th>
          <td
            v-for="col in definition.columns"
            :key="col.key"
            class="monitoring-matrix__cell"
            :class="row ? `matrix-cell--${row.key}` : ''"
          >
            <MonitoringSkeleton
              v-if="loading"
              class="monitoring-count-skeleton"
              variant="h1"
            />
            <button
              v-else
              type="button"
              class="monitoring-count monitoring-count--large"
              :class="{ 'monitoring-count--zero': cellValue(row, col) === 0 }"
              :disabled="cellValue(row, col) === 0"
              @click="debouncedClick(row, col)"
            >
              {{ cellValue(row, col) }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </el-card>
</template>

<script>
  import debounce from 'lodash.debounce';
  import MonitoringBlockHint from './MonitoringBlockHint.vue';
  import MonitoringSkeleton from './MonitoringSkeleton.vue';

  export default {
    name: 'MonitoringMatrixBlock',
    components: { MonitoringBlockHint, MonitoringSkeleton },
    props: {
      definition: {
        type: Object,
        required: true,
      },
      data: {
        type: Object,
        default: () => ({}),
      },
      urgency: {
        type: String,
        default: 'safe',
        validator: (v) => ['critical', 'warning', 'safe'].includes(v),
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        debouncedClick: null,
      };
    },
    computed: {
      urgencyClass() {
        return `urgency-${this.urgency}`;
      },
      // A status-less block (Block 1) renders a single unlabelled row.
      rows() {
        return this.definition.rows || [null];
      },
    },
    created() {
      this.debouncedClick = debounce(this.onClick, 300);
    },
    beforeDestroy() {
      this.debouncedClick.cancel();
    },
    methods: {
      cellValue(row, col) {
        const key = row ? `${row.key}_${col.dataKey}` : col.dataKey;
        return this.data[key] || 0;
      },
      onClick(row, col) {
        const count = this.cellValue(row, col);
        if (!count) return;
        const rowLabel = row ? this.$t(row.labelKey) : '';
        const colLabel = this.$t(col.labelKey);
        this.$emit('drill', {
          blockKey: this.definition.blockKey,
          statusKey: row ? row.key : null,
          bucketKey: col.key,
          count,
          label: rowLabel ? `${rowLabel} – ${colLabel}` : colLabel,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .monitoring-matrix-block {
    background: #2e3454;
    border-style: solid;
    border-color: rgba(#fff, 0.06);
    border-width: 1px;
    border-left-width: 4px;
    border-radius: 4px 12px 12px 4px;
    box-shadow: 0 10px 25px -5px rgba(#000, 0.3),
      0 4px 12px -2px rgba(#000, 0.2);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: translateY(-3px);
      background: #333a5e;
      border-color: rgba(#fff, 0.12);
      box-shadow: 0 20px 30px -8px rgba(#000, 0.45);
    }

    ::v-deep .el-card__body {
      padding: 20px 24px;
    }

    &.urgency-critical {
      border-left-color: #ef4444;
    }

    &.urgency-warning {
      border-left-color: #f59e0b;
    }

    &.urgency-safe {
      border-left-color: #10b981;
    }
  }

  .monitoring-matrix {
    width: 100%;
    border-collapse: separate;
    border-spacing: 8px;

    thead tr th {
      font-weight: 600;
      color: #94a3b8;
      padding: 4px 8px;
      text-align: center;
    }

    &__row-label {
      text-align: left;
      padding: 4px 8px;
    }

    &__cell {
      padding: 20px 24px;
      border: 1px solid rgba(#fff, 0.06);
      border-radius: 10px;
      background: rgba(#fff, 0.02);
      text-align: center;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

      &:hover {
        border-color: rgba(#fff, 0.12);
        background: rgba(#fff, 0.04);
      }
    }

    // Row-level tinted backgrounds (matching the stat-card palette by status).
    &.matrix-cell--tbv {
      background: rgba(#6366f1, 0.12);
      border-color: rgba(#6366f1, 0.25);
    }
    &.matrix-cell--tba {
      background: rgba(#64748b, 0.15);
      border-color: rgba(#64748b, 0.3);
    }
    &.matrix-cell--tbs {
      background: rgba(#ef4444, 0.1);
      border-color: rgba(#ef4444, 0.2);
    }
  }

  // Matches the count's footprint so cells don't resize when data lands.
  .monitoring-count-skeleton {
    width: 48px;
    margin: 0 auto;

    ::v-deep .el-skeleton__item {
      height: 26px;
    }
  }

  .monitoring-count {
    background: none;
    border: none;
    cursor: pointer;
    font-weight: 700;
    color: #6366f1;
    text-decoration: underline;
    padding: 0;

    &:hover {
      color: #818cf8;
    }

    &--large {
      font-size: 28px;
    }

    &:disabled,
    &--zero {
      color: #64748b;
      cursor: default;
      text-decoration: none;

      &:hover {
        color: #64748b;
      }
    }
  }

  .monitoring-block__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .title {
    color: #ffffff;
    font-weight: 600;
  }
</style>
